/* eslint-disable react-hooks/exhaustive-deps */
import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  editCrimeReferences2,
  editInvestigation,
} from "services/incidentService";
import { fetchAdminUnitDetail } from "services/organizationService";
import {
  addReceivingInvestigationFile,
  editReceivingInvestigationFile2,
} from "services/prosecutorPoliceService";
import {
  CrimeReference,
  CrimeTypeForInvestigationType,
  InvestigationFile,
  InvestigationFileBy,
  InvestigationFileStatus,
  Roles,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  isOpen: boolean;
  action: "Add" | "Edit";
  investigationFile?: InvestigationFile;
  incident?: CrimeReference;
  cr_number?: string;
  onClose: (refetch: boolean) => void;
  isForward?: boolean;
};

const defaultValue: InvestigationFile = {
  police_station_name: "",
  crime_reference_number: "",
  incident_registration_number: "",
  assign_clerk: "",
  assign_prosecutor: "",
  receiving_date: new Date(),
  status: "pending",
  prosecutor_decision_type: "pending",
  prosecutor_decision_status: "pending",
  police_station_id: null,
  is_efiling: false,
  investigation_file_upload: null,
  uu_id: 0,
  prosecutor_registration_number: "",
  investigationfileby: null,
  documents: [],
};

const InvestigationFileForm: FC<Props> = ({
  isOpen,
  action,
  investigationFile,
  incident,
  cr_number,
  onClose,
  isForward = false,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [formState, setFormState] = useState<InvestigationFile>(
    investigationFile || defaultValue,
  );

  const [adminUnitDetail, setAdminUnitDetail] = useState<any>();
  const [changeAdminUnit, setChangeAdminUnit] = useState<boolean>(false);
  const [newAdminUnitId, setNewAdminUnitId] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const isPolice = loginData?.role === "Member" || loginData?.is_police;
  const isCoordinator = hasRole(loginData?.roles, [
    Roles.coordinator,
    Roles.economicCoordinator,
    Roles.corruptionCoordinator,
    Roles.variousCoordinator,
  ]);

  useEffect(() => {
    if (incident) {
      setFormState({
        ...formState,
        incident_registration_number:
          incident?.Incident_Registration_number ?? "",
        crime_reference_number: cr_number ?? "",
        status: "forward_to_coordinator",
      });
    } else if (investigationFile) {
      // let dateValue = investigationFile?.receiving_date
      //   ? new Date(investigationFile.receiving_date)
      //   : undefined;
      // if (dateValue && isNaN(dateValue.getTime())) {
      //   // Handle invalid date – set to undefined or a fallback
      //   dateValue = undefined;
      // }

      setFormState({
        ...investigationFile,
        receiving_date: investigationFile?.receiving_date || undefined,
      });
    }
  }, [investigationFile, incident, cr_number]);

  useEffect(() => {
    if (isPolice) {
      const org = (loginData as any)?.organization;
      setFormState({
        ...formState,
        police_station_id: org?.uu_id,
        police_station_name: org?.name,
      });
    }
  }, [isPolice]);

  useEffect(() => {
    if (loginData) {
      fetchAdminUnitDetail(loginData?.organization?.admin_unit).then((data) => {
        if (data) {
          setAdminUnitDetail(data);
        }
      });
    }
  }, [loginData]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const submitAction =
      action === "Edit"
        ? editReceivingInvestigationFile2
        : addReceivingInvestigationFile;

    let newStatus: keyof typeof InvestigationFileStatus = "pending";
    switch (investigationFile?.status) {
      case "pending":
        newStatus = "forward_to_coordinator";
        break;
      case "forward_to_coordinator":
        newStatus = investigationFile?.assign_clerk
          ? "assigned_to_prosecutor"
          : "assigned_to_clerk";
        break;
    }

    submitAction({
      ...formState,
      incident_registration_number: formState.incident_registration_number,
      is_efiling: isPolice ? true : formState.is_efiling || false,
      police_station_id: (loginData as any)?.organization?.uu_id,
      police_station_name: isPolice
        ? (loginData as any)?.organization?.name
        : formState.police_station_name,
      status:
        action === "Add"
          ? isPolice
            ? "forward_to_coordinator"
            : "assigned_to_clerk"
          : isPolice
            ? "forward_to_coordinator"
            : newStatus,
      org_id: loginData?.org_unit || "",
      admin_unit_id: changeAdminUnit
        ? newAdminUnitId
        : loginData?.admin_unit ||
          (loginData as any)?.organization?.admin_unit ||
          "",
      report_application_number: incident?.report_application_number,
      incident_application_number: incident?.incident_application_number,

      investigationfileby:
        formState.investigationfileby === ("pending" as any)
          ? null
          : formState.investigationfileby,
    })
      .then(() => {
        showTooltip(
          action === "Edit"
            ? t("successfully_updated")
            : t("successfully_submitted"),
          "success",
        );

        if (incident?.uu_id)
          editInvestigation({
            id: incident?.uu_id,
            CR_number: formState?.crime_reference_number,
            Incident_Registration_number:
              formState?.incident_registration_number,
          });

        if (incident?.uu_id)
          editCrimeReferences2(incident?.uu_id?.toString(), {
            id: incident?.uu_id,
            CR_number: formState?.crime_reference_number,
            Incident_Registration_number:
              formState?.incident_registration_number,
          });
        setFormState(defaultValue);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  const handleFile = (file: File | null) => {
    setFormState({ ...formState, investigation_file: file });
  };

  if (!isOpen) return null;

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {isForward
            ? t("forward_to_investigation")
            : `${t(action.toLowerCase())} ${t("investigation_file")}`}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D] w-[400px]">
          {t("publishing_industries_placeholder")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4 mt-4">
            {isCoordinator && (
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("investigation_file_by")}
                </label>
                <select
                  className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                  name="investigationfileby"
                  value={formState?.investigationfileby ?? ""}
                  onChange={handleChange}
                  required
                >
                  <option value={""}>
                    {t("select_investigation_file_by")}
                  </option>
                  {Object.entries(InvestigationFileBy).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {isCoordinator && (
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("crime_type")}
                </label>
                <select
                  className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                  name="crime_type"
                  value={formState?.crime_type ?? ""}
                  onChange={handleChange}
                  required
                >
                  <option value={""}>{t("select_crime_type")}</option>
                  {Object.entries(CrimeTypeForInvestigationType).map(
                    ([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ),
                  )}
                </select>
              </div>
            )}
            {(isPolice || isCoordinator) && (
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {isPolice ? t("sent_date") : t("receiving_date")}
                </label>
                <input
                  type="date"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="receiving_date"
                  value={
                    formState.receiving_date
                      ? typeof formState.receiving_date === "string"
                        ? formState.receiving_date
                        : formState.receiving_date.toISOString().split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                />
              </div>
            )}

            {(isPolice || isCoordinator) && (
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("police_station_name")}
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="police_station_name"
                  value={formState.police_station_name}
                  onChange={handleChange}
                  disabled={isPolice ? true : false}
                />
              </div>
            )}

            {isPolice && (
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("crime_type")}
                </label>
                <select
                  className="border capitalize border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                  name="crime_type"
                  value={formState?.crime_type ?? ""}
                  onChange={handleChange}
                >
                  <option value={""}>{t("select_crime_type")}</option>
                  {Object.entries(CrimeTypeForInvestigationType).map(
                    ([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ),
                  )}
                </select>
              </div>
            )}
            {(isPolice || isCoordinator) && (
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("crime_reference_number")}
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="crime_reference_number"
                  value={formState.crime_reference_number}
                  onChange={handleChange}
                  disabled={
                    cr_number || investigationFile?.crime_reference_number
                      ? true
                      : false
                  }
                />
              </div>
            )}
            {(isPolice || isCoordinator) && (
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("incident_registration_number")}
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="incident_registration_number"
                  value={formState.incident_registration_number}
                  onChange={handleChange}
                />
              </div>
            )}
            {isCoordinator && investigationFile?.assign_clerk && (
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("assign_prosecutor")}
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="assign_prosecutor"
                  value={formState.assign_prosecutor}
                  onChange={handleChange}
                />
              </div>
            )}

            {isPolice && (
              <div className="flex items-center mt-4">
                <input
                  id="changeAdinUnit"
                  type="checkbox"
                  checked={changeAdminUnit}
                  onChange={(e) => {
                    setChangeAdminUnit(e.target.checked);
                    setNewAdminUnitId(null);
                  }}
                  className="mr-2"
                />
                <label
                  htmlFor="changeAdinUnit"
                  className="text-sm text-gray-700"
                >
                  {t("change_admin_unit")}
                </label>
              </div>
            )}

            {isPolice &&
              changeAdminUnit &&
              adminUnitDetail?.child_admin_units?.units && (
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    {t("admin_unit")}
                  </label>
                  <select
                    className="border capitalize border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                    value={newAdminUnitId ?? ""}
                    onChange={(e) => setNewAdminUnitId(e.target.value)}
                  >
                    <option value={""}>{t("select_admin_unit")}</option>
                    {(adminUnitDetail?.child_admin_units?.units ?? []).map(
                      (unit: { uuid: string; unit_name: string }) => (
                        <option key={unit?.uuid} value={unit?.uuid}>
                          {unit?.unit_name}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              )}
            {isPolice && (
              <div className="flex flex-col mt-4 w-full">
                <FileUpload
                  label={t("investigation_file")}
                  fileTypes="*"
                  info={t("upload_investigation_file")}
                  required={false}
                  onChange={handleFile}
                />
              </div>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                        text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                            ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  {t("loading")}
                </div>
              ) : isForward ? (
                t("forward")
              ) : (
                `${t(action.toLowerCase())} ${t("investigation_file")}`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvestigationFileForm;
